import { useTable } from 'react-table'
import InternalLink from '../common/internalLink'
import clsx from 'clsx'
import Button from '#app/components/buttons/button'
import TypographyLabel from '#app/components/typography/TypographyLabel'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	OpenInNewIcon,
	VerifiedIcon,
	WebsiteIcon,
} from '../icons'
import { useMemo, useRef } from 'react'
import { truncateStringInMiddle } from '#app/utils/misc.tsx'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useTheme } from '#app/utils/providers/themeProvider'

const getTwitterHandle = (twitterURL: string) => {
	try {
		const urlElems = twitterURL.split('/')
		if (urlElems[urlElems.length - 1])
			return '@' + urlElems[urlElems.length - 1]
		return ''
	} catch (e) {
		return ''
	}
}

const ArtistRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center gap-x-3 p-2">
			<img
				src={row.original['src']}
				alt={row.original['name']}
				height={54}
				width={54}
				className="objet-cover h-[56px] w-[56px] rounded-full"
			/>
			<div className="truncate break-words">
				<InternalLink to={`/artistProfile/${row.original.id}`} prefetch="none">
					<Button variant="tertiary">
						<TypographyLabel
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{row.original.name}
						</TypographyLabel>
					</Button>
				</InternalLink>
			</div>
			<VerifiedIcon size={16} />
		</div>
	)
}

const LinksRow = ({ value, column, row }) => {
	return row.original.socials ? (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			{row.original.socials.website && (
				<a href={row.original.socials.website} target="_blank">
					<Button variant="tertiary">
						<WebsiteIcon className="h-4 w-4" aria-hidden="true" />
					</Button>
				</a>
			)}
		</div>
	) : (
		''
	)
}

const DiDsRow = ({ value, column, row }) => {
	return row.original.wallets && row.original.wallets[0] ? (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			{row.original.wallets[0].name && (
				<a
					href={`https://etherscan.io/address/${row.original.wallets[0].name}`}
					target="_blank"
				>
					{row.original.wallets[0].displayName
						? row.original.wallets[0].displayName
						: truncateStringInMiddle(row.original.wallets[0].name)}
				</a>
			)}
		</div>
	) : (
		''
	)
}

const SignatureRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			<InternalLink to={row.original.signatures} prefetch="none">
				<Button variant="tertiary">
					<OpenInNewIcon className="h-4 w-4" aria-hidden="true" />
				</Button>
			</InternalLink>
		</div>
	)
}

export default function DataTable({
	data,
	loading = false,
	showFilter = true,
	showButton = true,
	sortRow = 'date',
	showStats = false,
	walletId = null,
	entryTitle = 'NFT',
}) {
	const tableContainerRef = useRef(null)
	const [theme] = useTheme()

	const columns = [
		{
			Header: 'Artist',
			id: 'name',
			Cell: ArtistRow,
		},
		{
			Header: 'X Handle',
			id: 'xHandle',
			accessor: row => (row.socials ? getTwitterHandle(row.socials.x) : ''),
		},
		{
			Header: 'External Links',
			id: 'externalLinks',
			Cell: LinksRow,
		},
		{
			Header: 'Top Collections',
			id: 'topCollections',
			accessor: row =>
				row.topCollections
					? row.topCollections
							.slice(0, 3)
							.map(collection => collection.name)
							.join(', ')
					: '',
		},
		{
			Header: 'Public DIDs',
			id: 'publicDIDs',
			Cell: DiDsRow,
		},
		{
			Header: 'Proof',
			id: 'signatures',
			Cell: SignatureRow,
		},
	]

	//  // Get state and functions for building table
	const tableData = useMemo(() => data, [data])

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		rows,
		prepareRow,
	} = useTable({
		columns,
		data: tableData,
		autoResetHiddenColumns: false,
	})

	return (
		<div className="overflow-x-auto">
			{loading ? (
				<SkeletonTheme
					baseColor={
						theme === 'light' || theme === undefined ? '#f5f5f5' : '#383838'
					}
					highlightColor={
						theme === 'light' || theme === undefined ? '#f8f8f8' : '#212121'
					}
				>
					<Skeleton count={5} className="my-2 h-12" />
				</SkeletonTheme>
			) : (
				<table {...getTableProps()} className="min-w-full table-auto">
					<thead className="py-6">
						{headerGroups.map((headerGroup, headerRowGroupIndex) => (
							<tr
								key={`header-table-row-${headerRowGroupIndex}`}
								className="text-textSecondaruLight border-b border-grey300 font-abc text-paragraphSmall leading-6 dark:text-textSecondaryDark"
							>
								{headerGroup.headers.map((column, headerGroupIndex) => {
									return (
										<th
											{...column.getHeaderProps()}
											key={`table-header-${headerRowGroupIndex}-${headerGroupIndex}`}
											scope="col"
											className={clsx(
												column.isSorted && 'underline',
												column.Header === 'Artist' &&
													'ml-2 inline min-w-[268px]',
												column.Header === 'X Handle' &&
													'min-w-[180px] md:table-cell',
												column.Header === 'External Links' &&
													'min-w-[20px] md:table-cell',
												column.Header === 'Top Collections' &&
													'min-w-[222px] md:table-cell',
												column.Header === 'Public DIDs' &&
													'min-w-[222px] md:table-cell',
												column.Header === 'Proof' && 'md:table-cell',
												'relative h-10 text-ellipsis py-0 pl-0 pr-0 text-center align-text-bottom text-paragraphSmallBold font-normal sm:pl-0',
											)}
										>
											{column.render('Header')}
											<span className="absolute top-[5px]">
												{column.isSorted ? (
													column.isSortedDesc ? (
														<ArrowDownIcon className="ml-[2px] h-3 w-3" />
													) : (
														<ArrowUpIcon className="ml-[2px] h-3 w-3" />
													)
												) : (
													''
												)}
											</span>
										</th>
									)
								})}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{rows.map((row, rowIndex) => {
							prepareRow(row)
							return (
								<tr
									key={`table-row-${rowIndex}`}
									className={clsx(
										'h-[72px] border-b border-dotted border-grey500 text-sm leading-6 text-textPrimaryLight hover:bg-grey100 dark:text-textPrimaryDark dark:hover:bg-[#161616]',
									)}
								>
									{row.cells.map((cell, cellIndex) => {
										return (
											<td
												key={`table-row-${rowIndex}-cell-${cellIndex}`}
												className={clsx(
													cell.column.Header === 'Artist' && 'md:table-cell',
													cell.column.Header === 'X Handle' && 'md:table-cell',
													cell.column.Header === 'External Links' &&
														'md:table-cell',
													cell.column.Header === 'Top Collections' &&
														'md:table-cell',
													cell.column.Header === 'Public DIDs' &&
														'md:table-cell',
													cell.column.Header === 'Blockchain' &&
														'md:table-cell',
													cell.column.Header === 'Proof' && 'md:table-cell',
													'whitespace-no-wrap px-0 py-0 text-center',
												)}
											>
												{cell.render('Cell')}
											</td>
										)
									})}
								</tr>
							)
						})}
					</tbody>
				</table>
			)}
		</div>
	)
}
